import type { InteractionMode, Loadout, ReplayLog, StarMetric } from '@full-elevator/sim';
import { BASE_LOADOUT, STANDARD_LIFT, UPGRADE_SYSTEMS } from '@full-elevator/sim';
import { COACHED_LEVELS, lessonIds } from './coach.js';

/**
 * Local persistence: progress, the interaction mode, and the replay
 * logs a playtest produces. Everything is namespaced and versioned so a schema
 * change can be detected rather than silently misread.
 */
const KEY = 'full-elevator.v1';

export interface LevelRecord {
  /** The best graded value: milliseconds on a time level, points on a service one. */
  readonly bestMs: number;
  readonly stars: number;
  readonly plays: number;
  /** The shift's bonus goal met at least once (ADR-0040). Absent on older saves. */
  readonly bonus?: true;
}

export interface SaveData {
  version: 1;
  mode: InteractionMode;
  records: Record<string, LevelRecord>;
  /** Archetypes the player has already been introduced to, so cards show once. */
  seenArchetypes: string[];
  /** Buildings already introduced. Same idea, one level up. */
  seenBuildings: string[];
  /**
   * Playtest switch: every level open, regardless of what has been finished.
   *
   * Replaying thirteen levels you already understand to reach the one you are
   * testing costs an hour and teaches nothing, so this exists. It is a testing
   * affordance and not a game feature — it must come out before anyone outside
   * the project plays, and it is listed in KNOWN_ISSUES so it does not quietly
   * stay. Progression itself is untouched: the flag only widens the gate.
   */
  unlockAll: boolean;
  /**
   * Developer tools have been used on this save since it was last reset:
   * every level unlocked, or coins added. Turning the unlock off again does
   * not clear it, because what it opened or bought stays. Every run kept from
   * this save carries it, so a tester's runs are told from a newcomer's.
   */
  devTools: boolean;
  /** Soft currency. One of them, and there will not be a second before v0.9. */
  coins: number;
  /**
   * Hardware fitted to each lift, by the lift's id (ADR-0027): every building
   * has its own, and what was bought for one stays in it. `standard` is the
   * Office Tower's — and every part bought before ADR-0027, which is where a
   * save from then puts it.
   */
  loadouts: Record<string, Loadout>;
  /** Kept small on purpose; export and clear after a playtest session. */
  replays: ReplayLog[];
  /** Sound and vibration (v0.8). On unless the player turns them off. */
  sound: boolean;
  /** The music (ADR-0056), apart from the sounds. On unless the player turns it off. */
  music: boolean;
  /**
   * Lessons of the guided first shifts already done (ADR-0030), so each is
   * shown once. Settings can empty it to be guided again.
   */
  coached: string[];
  /**
   * Taps go into a queue of stops, in the player's order, instead of the last
   * tap sending the car (ADR-0052). Null until the player has been offered the
   * choice, which counts as no: the lift goes to the last floor tapped.
   */
  tapAhead: boolean | null;
  /** The power-ups' card has been read (ADR-0055), so it shows once. */
  metPowers: boolean;
}

const EMPTY: SaveData = {
  version: 1,
  mode: 'dispatch',
  records: {},
  seenArchetypes: [],
  seenBuildings: [],
  unlockAll: false,
  devTools: false,
  coins: 0,
  loadouts: {},
  replays: [],
  sound: true,
  music: true,
  coached: [],
  tapAhead: null,
  metPowers: false,
};

/** A save with nothing in it: what a first launch, and a reset, start from. */
export const freshSave = (): SaveData => ({
  ...EMPTY,
  records: {},
  seenArchetypes: [],
  seenBuildings: [],
  unlockAll: false,
  devTools: false,
  loadouts: {},
  replays: [],
  coached: [],
});

/** A saved loadout can name a system that no longer exists, or a tier that does not. */
function readLoadout(value: unknown): Loadout {
  const raw = (value ?? {}) as Record<string, unknown>;
  const out: Record<string, number> = { ...BASE_LOADOUT };
  for (const system of UPGRADE_SYSTEMS) {
    const tier = raw[system];
    out[system] = typeof tier === 'number' && tier >= 0 ? Math.floor(tier) : 0;
  }
  return out as Loadout;
}

/**
 * Every lift's loadout. A save from before ADR-0027 has one `loadout`, bought
 * when there was one lift: it is the standard lift's, the Office Tower's, and
 * the buildings after it start on their own stock cars.
 */
function readLoadouts(value: unknown, legacy: unknown): Record<string, Loadout> {
  const out: Record<string, Loadout> = {};
  if (typeof value === 'object' && value !== null) {
    for (const [lift, loadout] of Object.entries(value as Record<string, unknown>)) {
      out[lift] = readLoadout(loadout);
    }
  }
  if (legacy !== undefined && out[STANDARD_LIFT.id] === undefined) {
    out[STANDARD_LIFT.id] = readLoadout(legacy);
  }
  return out;
}

/**
 * Lessons already done (ADR-0030). A save from before the guided shifts has
 * none recorded, but a level it has finished has been learned the hard way:
 * its lessons count as done, so nobody is walked through what they can do.
 */
function readCoached(parsed: Partial<SaveData>): string[] {
  if (Array.isArray(parsed.coached)) return parsed.coached.filter((id) => typeof id === 'string');
  const records = parsed.records ?? {};
  return COACHED_LEVELS.filter((id) => records[id] !== undefined).flatMap(lessonIds);
}

/** What is fitted to one lift: its stock car until something is bought for it. */
export function loadoutFor(data: SaveData, liftId: string): Loadout {
  return data.loadouts[liftId] ?? BASE_LOADOUT;
}

export function withLoadout(data: SaveData, liftId: string, loadout: Loadout): SaveData {
  return { ...data, loadouts: { ...data.loadouts, [liftId]: loadout } };
}

export function load(): SaveData {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return freshSave();
    const parsed = JSON.parse(raw) as SaveData;
    if (parsed.version !== 1) return freshSave();
    return {
      version: 1,
      // Dispatch is the game now (ADR-0003). A save written while the
      // experiment was running can still say `select`, and honouring it would
      // strand that player in a mode with no way out — the toggle is gone.
      mode: 'dispatch',
      records: parsed.records ?? {},
      seenArchetypes: Array.isArray(parsed.seenArchetypes) ? parsed.seenArchetypes : [],
      // Absent in saves written before buildings existed, which is every save
      // from a v0.4 playtest — those players meet the Grand Hotel properly
      // rather than arriving in it with no explanation.
      seenBuildings: Array.isArray(parsed.seenBuildings) ? parsed.seenBuildings : [],
      unlockAll: parsed.unlockAll === true,
      // Absent before v0.8's mark. A save with every level open has used them;
      // coins added before the mark existed cannot be told.
      devTools: parsed.devTools === true || parsed.unlockAll === true,
      coins: typeof parsed.coins === 'number' && parsed.coins >= 0 ? Math.floor(parsed.coins) : 0,
      loadouts: readLoadouts(
        (parsed as { loadouts?: unknown }).loadouts,
        (parsed as { loadout?: unknown }).loadout,
      ),
      replays: Array.isArray(parsed.replays) ? parsed.replays : [],
      // Absent before v0.8, when the game made no sound to turn off.
      sound: parsed.sound !== false,
      // Absent before ADR-0056, when there was no music to turn off.
      music: parsed.music !== false,
      coached: readCoached(parsed),
      // Absent before ADR-0052: not offered yet.
      tapAhead: typeof parsed.tapAhead === 'boolean' ? parsed.tapAhead : null,
      metPowers: parsed.metPowers === true,
    };
  } catch {
    // A corrupt save must never block a playtest; start clean instead.
    return freshSave();
  }
}

export function save(data: SaveData): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    // Quota or private mode. Progress is a convenience here, not a requirement.
  }
}

export function reset(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing useful to do */
  }
}

/** Record a finished run, keeping the best result per level. */
/**
 * Fold one finished run into the level's record.
 *
 * Only a win can set a best, and "best" means what the level grades on: the
 * lowest time, or the highest service score. Until 2026-10-14 this kept the
 * *minimum* of whatever it was given, which on a service level kept the worst
 * score, and let a run that failed early pass as the fastest time.
 */
export function recordRun(
  data: SaveData,
  levelId: string,
  value: number,
  stars: number,
  metric: StarMetric,
  bonus = false,
): SaveData {
  const previous = data.records[levelId];
  const hadWin = (previous?.stars ?? 0) > 0;
  const won = stars > 0;
  const better =
    metric === 'service' ? value > (previous?.bestMs ?? 0) : value < (previous?.bestMs ?? 0);
  const next: LevelRecord = {
    bestMs: won && (!hadWin || better) ? value : (previous?.bestMs ?? 0),
    stars: Math.max(previous?.stars ?? 0, stars),
    plays: (previous?.plays ?? 0) + 1,
    // Once met, kept: a medal is not taken back by a later run.
    ...(bonus || previous?.bonus ? { bonus: true as const } : {}),
  };
  return { ...data, records: { ...data.records, [levelId]: next } };
}

/**
 * What this run was, against the record it is about to be folded into: the first
 * time the level was cleared, a new best, or neither. Asked before `recordRun`.
 */
export function runMilestone(
  previous: LevelRecord | undefined,
  value: number,
  stars: number,
  metric: StarMetric,
): 'first-clear' | 'new-best' | null {
  if (stars === 0) return null;
  if (!previous || previous.stars === 0) return 'first-clear';
  const better = metric === 'service' ? value > previous.bestMs : value < previous.bestMs;
  return better ? 'new-best' : null;
}

/** Keep the most recent sessions only — a phone should not accumulate megabytes. */
const MAX_REPLAYS = 40;

export function addReplay(data: SaveData, log: ReplayLog): SaveData {
  return { ...data, replays: [...data.replays, log].slice(-MAX_REPLAYS) };
}

export function markArchetypesSeen(data: SaveData, archetypes: readonly string[]): SaveData {
  return { ...data, seenArchetypes: [...new Set([...data.seenArchetypes, ...archetypes])] };
}

export function markBuildingSeen(data: SaveData, building: string): SaveData {
  return { ...data, seenBuildings: [...new Set([...(data.seenBuildings ?? []), building])] };
}

export function isUnlocked(data: SaveData, index: number, levelIds: readonly string[]): boolean {
  if (data.unlockAll) return true;
  if (index === 0) return true;
  const previous = levelIds[index - 1];
  return previous !== undefined && data.records[previous] !== undefined;
}
